import { IExamFile } from '../types/exam-checker';
import { BaseModel } from './base-model';
import { PromiseExtended, Table } from 'dexie';
import { Filesystem } from '@capacitor/filesystem';

const SCHEMA = {
  examFiles: '++id, localUrl, examId, studentId, isUploaded',
};

const SCHEMA_VERSION = 4;

export type ExamFileRecord = IExamFile

class ExamCheckerDB extends BaseModel {
  examFiles!: Table<IExamFile, number>;

  constructor() {
    super('examCheckerdb');
    this.version(SCHEMA_VERSION).stores(SCHEMA);
  }

  async getUniqueStudentIdsByExamId(examId: string): Promise<string[]> {
    const files = await this.examFiles.where('examId').equals(examId).toArray();
    return Array.from(
      new Set(
        files
          .map((file) => file.studentId)
          .filter((id): id is string => id !== undefined),
      ),
    );
  }

  async deleteExamFilesByStudentIdAndExamId(
    studentId: string,
    examId: string,
  ): Promise<number> {
    return this.examFiles.where({ studentId, examId }).delete();
  }

  /**
   * checks if any file is uploaded for a given student and exam.
   *
   * used  for conditional rendering of UI elements, such as hiding delete buttons
   * or other actions that should not be available once a file is uploaded.
   */
  async isAnyFileUploadedForStudentIdAndExamId(
    studentId: string,
    examId: string,
  ): Promise<boolean> {
    const files = await this.examFiles
      .where({ studentId, examId })
      .filter((file) => file.isUploaded === true)
      .toArray();
    return files.length > 0;
  }

  getExamFilesByExamId(examId: string): PromiseExtended<ExamFileRecord[]> {
    return this.examFiles.where('examId').equals(examId).toArray();
  }

  getExamFilesByExamAndStudentId(
    examId: string,
    studentId: string,
  ): PromiseExtended<ExamFileRecord[]> {
    return this.examFiles.where({ examId, studentId }).toArray();
  }

  bulkAddExamFiles(examFiles: IExamFile[]): PromiseExtended<number> {
    return this.examFiles.bulkAdd(examFiles);
  }

  setExamFileData(
    fileId: number,
    data: Partial<IExamFile>,
  ): PromiseExtended<number> {
    return this.examFiles.update(fileId, data);
  }

  updateExamFileByUrl(
    localUrl: string,
    dataToUpdate: Partial<IExamFile>,
  ): PromiseExtended<number> {
    return this.examFiles
      .where('localUrl')
      .equals(localUrl)
      .modify(dataToUpdate);
  }

  deleteExamFileByUrl(localUrl: string): PromiseExtended<number> {
    return this.examFiles.where('localUrl').equals(localUrl).delete();
  }

  async deleteExamFileByStudentUuid(studentUuid: string): Promise<number> {
    const examFilesOfStudent = await this.examFiles
      .where('studentId')
      .equals(studentUuid)
      .toArray();

    examFilesOfStudent.forEach((file) => {
      const path = file.localUrl.replace(
        'https://localhost/_capacitor_file_',
        '',
      );
      Filesystem.deleteFile({
        path,
      });
    });

    return this.examFiles.where('studentId').equals(studentUuid).delete();
  }
}

export const examCheckerDB = new ExamCheckerDB();
